import React, { useEffect, useMemo, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { API_BASE_URL } from "../config";
import { useAuth } from "../auth";


const styles = `
  body {
    background-color: #f5f5f5;
  }

  .service-monitor-page {
    background-color: #f5f5f5;
    min-height: 100vh;
    padding: 20px;
  }

  .dashboard-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 30px;
    border-radius: 10px;
    margin-bottom: 30px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .probe-card {
    background: white;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s, box-shadow 0.2s;
  }

  .probe-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .probe-header {
    border-bottom: 2px solid #667eea;
    padding-bottom: 10px;
    margin-bottom: 15px;
  }

  .probe-name {
    font-size: 1.5em;
    font-weight: bold;
    color: #667eea;
  }

  .machine-name {
    font-size: 0.9em;
    color: #666;
  }

  .service-item {
    padding: 10px;
    margin: 8px 0;
    border-radius: 5px;
    border-left: 4px solid #ccc;
    background: #f8f9fa;
    transition: all 0.3s;
  }

  .service-item.status-running {
    border-left-color: #28a745;
    background: #d4edda;
  }

  .service-item.status-stopped {
    border-left-color: #dc3545;
    background: #f8d7da;
  }

  .service-item.status-error {
    border-left-color: #ffc107;
    background: #fff3cd;
  }

  .service-name {
    font-weight: bold;
    color: #333;
  }

  .service-status {
    float: right;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.85em;
    font-weight: bold;
  }

  .status-badge-running {
    background: #28a745;
    color: white;
  }

  .status-badge-stopped {
    background: #dc3545;
    color: white;
  }

  .status-badge-error {
    background: #ffc107;
    color: #333;
  }

  .connection-status {
    

    top: 20px;
    right: 20px;
    padding: 10px 20px;
    border-radius: 20px;
    font-weight: bold;
    z-index: 1000;
  }

  .connection-status.connected {
    background: #28a745;
    color: white;
  }

  .connection-status.disconnected {
    background: #dc3545;
    color: white;
  }

  .last-update {
    font-size: 0.85em;
    color: #666;
    margin-top: 10px;
  }

  .error-message {
    font-size: 0.85em;
    color: #dc3545;
    margin-top: 5px;
    font-style: italic;
  }

  .no-data {
    text-align: center;
    padding: 50px;
    color: #999;
    font-size: 1.2em;
  }
`;



type ServiceStatusInfo = {

  serviceName: string;
  status: string;
  displayName: string;
  timestamp: Date;
  probeServiceName: string;
  machineName: string;
  errorMessage: string;


};

type ProbeInfo = {

  
  ProbeServiceName: string;
  Services: ServiceStatusInfo[];
  MachineName: string;


};

const NO_SIGNAL_THRESHOLD_MS = 6 * 60 * 1000;
const NO_SIGNAL_CHECK_INTERVAL_MS = 30 * 1000;

function getStatusClass(status :string) {
  const value = String(status || "").toLowerCase();

  if (value === "running") return "status-running";
  if (value === "stopped") return "status-stopped";

  return "status-error";
}

function getStatusBadgeClass(status:string) {
  const value = String(status || "").toLowerCase();

  if (value === "running") return "status-badge-running";
  if (value === "stopped") return "status-badge-stopped";

  return "status-badge-error";
}

function formatDate(value : Date) {
  if (!value) return "Unbekannt";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Unbekannt";
  }

  return date.toLocaleString("de-DE");
}

function ServiceItem({ service }: { service: ServiceStatusInfo }) {
  const status = service.status || "Unknown";
  const displayName =
    service.displayName || service.serviceName || "Unbekannter Dienst";
  const errorMessage = service.errorMessage;

  return (
    <div className={`service-item ${getStatusClass(status)}`}>
      <span className="service-name">{displayName}</span>
      <span className={`service-status ${getStatusBadgeClass(status)}`}>
        {status}
      </span>
      <div style={{ clear: "both" }} />
      {errorMessage ? (
        <div className="error-message">⚠️ {errorMessage}</div>
      ) : null}
        <div className="last-update">
          Letzte Aktualisierung: {formatDate(service.timestamp)}
        </div>
    </div>
  );
}

function ProbeCard({ probe }: { probe: ProbeInfo }) {
  return (
    <div className="col-xl-4 col-lg-6 col-md-12">
      <div className="probe-card">
        <div className="probe-header">
          <div className="probe-name">{probe.ProbeServiceName}</div>
          <div className="machine-name">🖥️ {probe.MachineName}</div>
        </div>

        <div className="services-list">
          {probe.Services.map((service: ServiceStatusInfo, index: number) => (
            <ServiceItem
              key={`${service.serviceName}-${index}`}
              service={service}
            />
          ))}
        </div>

      
      </div>
    </div>
  );
}

export function MonitorPage() {
  const [statusList, setStatusList] = useState<ServiceStatusInfo[]>([]);
  const [connectionStatus, setConnectionStatus] = useState({
    connected: false,
    text: "✗ Getrennt",
  });
  const { accessToken } = useAuth();

     const markStaleAsNoSignal = (newList: ServiceStatusInfo[] | null) => {
      const now = Date.now();

      setStatusList((current) => {
        let changed = false;

        const updated = (newList??current).map((item) => {
          const timestampValue = new Date(item.timestamp as unknown as string).getTime();
          if (Number.isNaN(timestampValue)) {
            return item;
          }

          const isStale = now - timestampValue > NO_SIGNAL_THRESHOLD_MS;
          if (!isStale || item.status === "No Signal") {
            return item;
          }

          changed = true;
          return { ...item, status: "No Signal" };
        });

        return changed ? updated : current;
      });
    };

  useEffect(() => {
    if (!connectionStatus.connected) {
      return;
    }

 

    // Run once immediately after connection and then periodically.
    markStaleAsNoSignal(null);
    const timerId = window.setInterval(markStaleAsNoSignal, NO_SIGNAL_CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(timerId);
    };
  }, [connectionStatus.connected]);

  useEffect(() => {
    let isMounted = true;
    let retryTimerId: ReturnType<typeof window.setTimeout> | undefined;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${API_BASE_URL}/ServiceMonitorHub`, {
        accessTokenFactory: () => accessToken || "",
      })
      .withAutomaticReconnect()
      .build();

    const setConnected = (connected: boolean) => {
      if (!isMounted) return;

      setConnectionStatus({
        connected,
        text: connected ? "✓ Verbunden" : "✗ Getrennt",
      });
    };

    connection.on("connected", () => {
      setConnected(true);
      console.log("Verbindung zum SignalR Hub hergestellt");
    });

    connection.on("UpdateServiceStatus", (newStatusList) => {
      console.log("Service-Status empfangen:", newStatusList);
      setStatusList(Array.isArray(newStatusList) ? newStatusList : []);
        markStaleAsNoSignal(Array.isArray(newStatusList) ? newStatusList : null);
    });

    connection.onclose(() => {
      setConnected(false);
      console.log("Verbindung zum SignalR Hub getrennt");
    });

    connection.onreconnecting(() => {
      setConnected(false);
      console.log("Versuche Wiederverbindung...");
    });

    connection.onreconnected(() => {
      setConnected(true);
      console.log("Wiederverbindung erfolgreich");
    });

    const startConnection = async () => {
      try {
        await connection.start();
        setConnected(true);
        var lastResults = await connection.invoke("JoinMonitorGroup");
        if (lastResults) {
          console.log("Letzter Status vom Server:", lastResults);
          setStatusList(Array.isArray(lastResults) ? lastResults : []);
          markStaleAsNoSignal(Array.isArray(lastResults) ? lastResults : null);

        }
        console.log("SignalR-Verbindung gestartet");
      } catch (error) {
        setConnected(false);
        console.error("Fehler beim Verbinden:", error);

        retryTimerId = window.setTimeout(startConnection, 5000);
      }
    };

    startConnection();

    return () => {
      isMounted = false;
      window.clearTimeout(retryTimerId);

      connection.off("connected");
      connection.off("UpdateServiceStatus");
      connection.stop().catch(() => {
        // Ignore shutdown errors during unmount.
      });
    };
  }, []);

  const groupedProbes = useMemo(() => {
    const grouped = new Map<string, ProbeInfo>();

    statusList.forEach((item) => {
      const probeName = item.probeServiceName;

      if (!grouped.has(probeName)) {
        grouped.set(probeName, {
          ProbeServiceName: probeName,
          MachineName: item.machineName || "Unbekannt",
          Services: [] as ServiceStatusInfo[]
        });
      }

      const probe = grouped.get(probeName);
      if (probe) {
        probe.Services.push(item);

       
      }
    });

    return Array.from(grouped.values());
  }, [statusList]);

  return (
    <div className="service-monitor-page">
      <style>{styles}</style>

      <span
        className={`connection-status ${connectionStatus.connected ? "connected" : "disconnected"
          }`}
      >
        {connectionStatus.text}
      </span>

      <div className="container-fluid mt-4">
      

        <div className="row">
          {groupedProbes.length === 0 ? (
            <div className="col-12 no-data">
              Warten auf Daten vom Probe-Service...
            </div>
          ) : (
            groupedProbes.map((probe) => (
              <ProbeCard key={probe.ProbeServiceName} probe={probe} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
